/**
 * ErrorVoiceControl — lets a signed-in user hear an audio version of Anaka's
 * error message (the pre-generated branded clip `/assets/errors/<scenario>.mp3`
 * when present, otherwise the browser's system voice; see {@link useErrorVoice}).
 *
 * It reads the persisted `inputMode` from profile settings:
 *   - voice mode → the clip auto-plays on mount; the control replays / stops
 *     (and doubles as a "tap to hear" affordance if autoplay is blocked).
 *   - text mode → no auto-play; the control is a quiet opt-in ("Listen").
 *
 * The button uses the chat input bar's `Mic` icon so the "voice" cue is
 * consistent across the app. Only mount this for authenticated users — guests
 * have no profile, and the surface is itself a sign-in gate.
 */
import { Mic, Square } from 'lucide-react';

import { useProfileSettings } from '@/hooks/v2/useProfileSettings';

import { useErrorVoice } from './useErrorVoice';

export interface ErrorVoiceControlProps {
  /** Scenario id — selects the branded clip `/assets/errors/<id>.mp3`. */
  scenarioId: string;
  /** Plain-text message — the speech-synthesis fallback. */
  message: string;
}

/** Where `scripts/generate-error-audio.ts` writes the branded clips. */
function clipSrc(scenarioId: string): string {
  return `/assets/errors/${scenarioId}.mp3`;
}

export function ErrorVoiceControl({ scenarioId, message }: ErrorVoiceControlProps) {
  const { data: profileSettings } = useProfileSettings();
  // Voice-mode users auto-play; text-mode users see the same control but opt in
  // by tapping (enabled drives auto-play only).
  const voiceEnabled = profileSettings?.inputMode === 'voice';
  const { available, playing, needsGesture, play, stop } = useErrorVoice({
    src: clipSrc(scenarioId),
    text: message,
    enabled: voiceEnabled,
  });

  if (!available) return null;

  const label = playing ? 'Stop' : needsGesture ? 'Tap to hear' : 'Listen';

  return (
    <button
      type="button"
      className={`es-voice ${playing ? 'is-speaking' : ''}`}
      onClick={playing ? stop : play}
      aria-label={playing ? 'Stop reading this message aloud' : 'Read this message aloud'}
      data-testid="error-voice"
    >
      {playing ? <Square aria-hidden="true" /> : <Mic aria-hidden="true" />}
      {label}
    </button>
  );
}

export default ErrorVoiceControl;
